// Sprint PKG-VIZ A1 (#792) — dagre hierarchical layout helper.
//
// Deterministic: dagre's ranking algorithm has no randomness, but its
// barycenter/median tie-breaking IS sensitive to the order nodes/edges
// are inserted into the graph. To make "same [nodes, edges] input →
// same output" hold regardless of the caller's array order (required
// by S3 / adjudication Q6, and by V2's structural-signature approach
// which is itself sorted-id-based), nodes and edges are inserted in a
// canonical sorted-id order here, not the input array's order.

import dagre from 'dagre';
import type { PKGNode, PKGEdge } from '@blueprint/shared';
import { LAYOUT_NODE_WIDTH, LAYOUT_NODE_HEIGHT, type PKGLayoutPositions } from './types.ts';

export type DagreDirection = 'LR' | 'TB' | 'RL' | 'BT';

export function computeDagreLayout(
  nodes: PKGNode[],
  edges: PKGEdge[],
  direction: DagreDirection = 'LR',
): PKGLayoutPositions {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: direction });

  const sortedNodes = [...nodes].sort((a, b) => a.id.localeCompare(b.id));
  const sortedEdges = [...edges].sort((a, b) =>
    a.source === b.source
      ? a.target === b.target
        ? a.edgeType.localeCompare(b.edgeType)
        : a.target.localeCompare(b.target)
      : a.source.localeCompare(b.source),
  );

  for (const node of sortedNodes) {
    graph.setNode(node.id, { width: LAYOUT_NODE_WIDTH, height: LAYOUT_NODE_HEIGHT });
  }
  for (const edge of sortedEdges) {
    // dagre requires both endpoints to be registered nodes.
    if (graph.hasNode(edge.source) && graph.hasNode(edge.target)) {
      graph.setEdge(edge.source, edge.target);
    }
  }

  dagre.layout(graph);

  const positions: PKGLayoutPositions = new Map();
  for (const node of nodes) {
    const laidOut = graph.node(node.id);
    positions.set(node.id, { x: laidOut.x, y: laidOut.y });
  }
  return positions;
}
